import React, { useState, useEffect, useMemo } from 'react';
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { RefreshCw, Loader2 } from "lucide-react";
import { CustomDataTable } from "@/components/table/CustomDataTable";
import { KolDataWithDates } from '@/types/table';
import { DataQualityTableProps, updateKolTrackerRow, transformKolTrackerData, groupDataByPic } from './dataQualityUtils';
import { GroupedDataQualityTable } from './GroupedDataQualityTable';
import { EnhancedTableCell } from "@/components/table/EnhancedTableCell";
import { 
  LANGUAGE_OPTIONS, 
  PLATFORM_OPTIONS, 
  TYPE_OPTIONS, 
  TIER_OPTIONS 
} from '@/components/kol/constants/kolPostingLinksConstants';

export const MissingContentIdTable: React.FC<DataQualityTableProps> = ({ onDataChange, groupByPic = false }) => {
  const [data, setData] = useState<KolDataWithDates[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [contentIdOptions, setContentIdOptions] = useState<string[]>([]);
  const { toast } = useToast();

  const fetchContentIds = async () => {
    try {
      const { data: contentIds, error } = await supabase
        .from('topics_business_units')
        .select('name')
        .order('name');

      if (error) throw error;

      const options = contentIds?.map(item => item.name) || [];
      setContentIdOptions(options);
    } catch (error) {
      console.error('Error fetching content IDs:', error);
    }
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      
      // Get total count
      const { count } = await supabase
        .from('kol_tracker')
        .select('*', { count: 'exact', head: true })
        .or('campaign_bu.is.null,campaign_bu.eq.[]');
      
      setTotalCount(count || 0);

      // Get all data without pagination
      const { data: rawData, error } = await supabase
        .from('kol_tracker')
        .select('*')
        .or('campaign_bu.is.null,campaign_bu.eq.[]')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const transformedData = transformKolTrackerData(rawData || []);
      setData(transformedData);
    } catch (error) {
      console.error('Error fetching missing content ID data:', error);
      toast({
        title: "Error",
        description: "Failed to load records with missing content IDs (campaign_bu)",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchContentIds();
    fetchData();
  }, []);

  const handleRowEdit = async (newRow: KolDataWithDates, oldRow: KolDataWithDates) => {
    try {
      setIsProcessing(true);
      
      const changedFields = Object.keys(newRow).filter(
        key => newRow[key as keyof KolDataWithDates] !== oldRow[key as keyof KolDataWithDates]
      );

      for (const field of changedFields) {
        await updateKolTrackerRow(
          newRow.id, 
          field as keyof KolDataWithDates, 
          newRow[field as keyof KolDataWithDates]
        );
      }

      toast({
        title: "Success",
        description: "Record updated successfully",
      });

      await fetchData();
      if (onDataChange) onDataChange();
      
      return newRow;
    } catch (error) {
      console.error("Error updating row:", error);
      toast({
        title: "Error",
        description: "Failed to update record",
        variant: "destructive"
      });
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const columns = useMemo(() => [
    {
      id: 'id',
      header: 'ID',
      accessorKey: 'id',
      editable: false,
      minWidth: 300,
      cell: (row: KolDataWithDates) => (
        <div className="font-mono text-xs text-muted-foreground" title={row.id}>
          {row.id}
        </div>
      )
    },
    {
      id: 'name',
      header: 'Name',
      accessorKey: 'name',
      editable: true,
      inputType: 'text' as const,
      minWidth: 150,
      cell: (row: KolDataWithDates) => (
        <EnhancedTableCell
          value={row.name}
          field="name"
          rowData={row}
          editable={true}
          inputType="text"
          onChange={async (field, value) => {
            const updatedRow = { ...row, [field]: value };
            await handleRowEdit(updatedRow, row);
          }}
        />
      )
    },
    {
      id: 'campaign_bu',
      header: 'Content ID',
      accessorKey: 'campaign_bu',
      editable: true,
      inputType: 'select' as const,
      selectOptions: contentIdOptions.map(id => ({ label: id, value: id })),
      minWidth: 200,
      cell: (row: KolDataWithDates) => {
        const currentValue = Array.isArray(row.campaign_bu) ? row.campaign_bu[0] || '' : row.campaign_bu || '';
        
        return (
          <EnhancedTableCell
            value={currentValue}
            field="campaign_bu"
            rowData={row}
            editable={true}
            inputType="select"
            selectOptions={contentIdOptions.map(id => ({ label: id, value: id }))}
            onChange={async (field, value) => {
              const updatedRow = { ...row, [field]: [value] };
              await handleRowEdit(updatedRow, row);
            }}
          />
        );
      }
    },
    {
      id: 'post_link',
      header: 'Post Link',
      accessorKey: 'post_link',
      editable: true,
      inputType: 'url' as const,
      minWidth: 200,
      cell: (row: KolDataWithDates) => (
        <EnhancedTableCell
          value={row.post_link}
          field="post_link"
          rowData={row}
          editable={true}
          inputType="url"
          onChange={async (field, value) => {
            const updatedRow = { ...row, [field]: value };
            await handleRowEdit(updatedRow, row);
          }}
        />
      )
    },
    {
      id: 'language',
      header: 'Language',
      accessorKey: 'language',
      editable: true,
      inputType: 'select' as const,
      selectOptions: LANGUAGE_OPTIONS.map(lang => ({ label: lang, value: lang })),
      minWidth: 120,
      cell: (row: KolDataWithDates) => (
        <EnhancedTableCell
          value={row.language}
          field="language"
          rowData={row}
          editable={true}
          inputType="select"
          selectOptions={LANGUAGE_OPTIONS.map(lang => ({ label: lang, value: lang }))}
          onChange={async (field, value) => {
            const updatedRow = { ...row, [field]: value };
            await handleRowEdit(updatedRow, row);
          }}
        />
      )
    },
    {
      id: 'platform',
      header: 'Platform',
      accessorKey: 'platform',
      editable: true,
      inputType: 'select' as const,
      selectOptions: PLATFORM_OPTIONS.map(platform => ({ label: platform, value: platform })),
      minWidth: 120,
      cell: (row: KolDataWithDates) => (
        <EnhancedTableCell
          value={row.platform}
          field="platform"
          rowData={row}
          editable={true}
          inputType="select"
          selectOptions={PLATFORM_OPTIONS.map(platform => ({ label: platform, value: platform }))}
          onChange={async (field, value) => {
            const updatedRow = { ...row, [field]: value };
            await handleRowEdit(updatedRow, row);
          }}
        />
      )
    },
    {
      id: 'type',
      header: 'Type',
      accessorKey: 'type',
      editable: true,
      inputType: 'select' as const,
      selectOptions: TYPE_OPTIONS.map(type => ({ label: type, value: type })),
      minWidth: 150,
      cell: (row: KolDataWithDates) => (
        <EnhancedTableCell
          value={row.type}
          field="type"
          rowData={row}
          editable={true}
          inputType="select"
          selectOptions={TYPE_OPTIONS.map(type => ({ label: type, value: type }))}
          onChange={async (field, value) => {
            const updatedRow = { ...row, [field]: value };
            await handleRowEdit(updatedRow, row);
          }}
        />
      )
    },
    {
      id: 'channel',
      header: 'Channel',
      accessorKey: 'channel',
      editable: true,
      inputType: 'text' as const,
      minWidth: 150,
      cell: (row: KolDataWithDates) => (
        <EnhancedTableCell
          value={row.channel}
          field="channel"
          rowData={row}
          editable={true}
          inputType="text"
          onChange={async (field, value) => {
            const updatedRow = { ...row, [field]: value };
            await handleRowEdit(updatedRow, row);
          }}
        />
      )
    },
    {
      id: 'expense',
      header: 'Expense',
      accessorKey: 'expense',
      editable: true,
      inputType: 'number' as const,
      minWidth: 100,
      cell: (row: KolDataWithDates) => (
        <EnhancedTableCell
          value={row.expense}
          field="expense"
          rowData={row}
          editable={true}
          inputType="number"
          onChange={async (field, value) => {
            const updatedRow = { ...row, [field]: value };
            await handleRowEdit(updatedRow, row);
          }}
        />
      )
    }
  ], [contentIdOptions]);

  const groupedData = useMemo(() => {
    if (groupByPic) {
      return groupDataByPic(data);
    }
    return {};
  }, [data, groupByPic]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Records Missing Content ID</h3>
          <p className="text-sm text-muted-foreground">
            {totalCount} record{totalCount !== 1 ? 's' : ''} found with missing or empty campaign_bu information
          </p>
        </div>
        <Button
          onClick={() => fetchData()}
          disabled={isLoading}
          variant="outline"
          size="sm"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {isProcessing && (
        <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 p-2 rounded">
          <Loader2 className="h-3 w-3 animate-spin" />
          Saving changes...
        </div>
      )}

      {groupByPic ? (
        <GroupedDataQualityTable
          groupedData={groupedData}
          columns={columns}
          loading={isLoading}
          onRowEdit={handleRowEdit}
        />
      ) : (
        <CustomDataTable
          data={data}
          columns={columns}
          loading={isLoading}
          onRowEdit={handleRowEdit}
          enableColumnResizing={true}
          enableSorting={true}
          showPagination={false}
        />
      )}
    </div>
  );
};
